/**
 * Asset Mapping — read-only view pairing FAIM assets with the telematics
 * equipment they are mapped to. Route: /asset-mapping
 *
 * Ports FAIM I's Asset Mapping screen as four tabs:
 *  - Mapped          — auto- or manually-mapped assets (persisted, live grid).
 *  - Awaiting Review  — candidate matches awaiting a human decision.
 *  - Rejected         — mappings a fleet manager rejected (persisted, live grid).
 *  - No Match         — telematics equipment with no asset match (Increment 2).
 *
 * The active tab and the cascading geography/brand filters all live in the URL
 * search params so the view is shareable and survives refresh. The two live
 * grids share one filter bar and one grid definition; only the query differs.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router';
import { graphql } from '../../gql';
import { useSafeMutation } from '../../api/useSafeMutation';
import { DataGrid } from '../../components/data-grid';
import { TextInput, SelectInput, PageHeader, LoadingOverlay, LocationMap, SectionHeading } from '../../components/ui';
import type { SelectOption } from '../../components/ui';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { TypedDocumentNode } from '@apollo/client';
import type { SearchMappedAssetsQueryVariables } from '../../gql/graphql';

// Reuses the exact Asset Search filter-options operation (same cascading,
// user-scoped master-data lists) — codegen dedupes the identical document.
const FILTER_OPTIONS_QUERY = graphql(`
  query AssetSearchFilterOptions(
    $areaId: Int
    $countryId: Int
    $businessLineId: Int
    $brandId: Int
  ) {
    assetSearchFilterOptions(
      areaId: $areaId
      countryId: $countryId
      businessLineId: $businessLineId
      brandId: $brandId
    ) {
      areas { id name }
      countries { id name }
      businessLines { id name }
      regions { id name }
      brands { id name }
      models { id name }
    }
  }
`);

const SEARCH_MAPPED_QUERY = graphql(`
  query SearchMappedAssets($filter: AssetMappingFilterInput) {
    searchMappedAssets(filter: $filter) {
      assetId
      assetCode
      serialNumber
      brandName
      modelName
      plantId
      plantName
      plantCode
      businessLineName
      regionName
      telematicsEquipmentId
      oemMake
      oemModel
      oemSerialNumber
      statusLocalizationKey
      isEditable
    }
  }
`);

const SEARCH_REJECTED_QUERY = graphql(`
  query SearchRejectedAssets($filter: AssetMappingFilterInput) {
    searchRejectedAssets(filter: $filter) {
      assetId
      assetCode
      serialNumber
      brandName
      modelName
      plantId
      plantName
      plantCode
      businessLineName
      regionName
      telematicsEquipmentId
      oemMake
      oemModel
      oemSerialNumber
      statusLocalizationKey
      isEditable
    }
  }
`);

const SEARCH_AWAITING_REVIEW_QUERY = graphql(`
  query SearchAwaitingReviewAssets($filter: AssetMappingFilterInput) {
    searchAwaitingReviewAssets(filter: $filter) {
      assetId
      assetCode
      serialNumber
      brandName
      modelName
      plantId
      plantName
      plantCode
      businessLineName
      regionName
      telematicsEquipmentId
      oemMake
      oemModel
      oemSerialNumber
      statusLocalizationKey
      isEditable
    }
  }
`);

const SEARCH_NO_MATCH_EQUIPMENT_QUERY = graphql(`
  query SearchNoMatchEquipment {
    searchNoMatchEquipment {
      telematicsEquipmentId
      oemMake
      oemModel
      oemSerialNumber
      lastSeenApiName
      countryName
      lastLocationDateTime
    }
  }
`);

const GET_AWAITING_REVIEW_EQUIPMENT_MATCHES_QUERY = graphql(`
  query GetAwaitingReviewEquipmentMatches($telematicsEquipmentId: Int!) {
    awaitingReviewEquipmentMatches(telematicsEquipmentId: $telematicsEquipmentId) {
      telematicsEquipmentId
      oemMake
      oemModel
      oemSerialNumber
      lastLocationLatitude
      lastLocationLongitude
      lastLocationDateTime
      otherBrandMatches {
        assetId
        assetCode
        serialNumber
        brandName
        modelName
        plantName
        isEditable
      }
    }
  }
`);

const SAVE_MANUAL_MAPPING_MUTATION = graphql(`
  mutation SaveManualMapping($telematicsEquipmentId: Int!, $assetId: Int!) {
    saveManualMapping(telematicsEquipmentId: $telematicsEquipmentId, assetId: $assetId) {
      success
      error { message }
    }
  }
`);

interface MappingRow {
  assetId: number;
  assetCode: string;
  serialNumber?: string | null;
  brandName: string;
  modelName: string;
  plantId: number;
  plantName: string;
  plantCode: string;
  businessLineName: string;
  regionName: string;
  telematicsEquipmentId: number;
  oemMake?: string | null;
  oemModel?: string | null;
  oemSerialNumber?: string | null;
  statusLocalizationKey: string;
  isEditable: boolean;
}

interface NoMatchRow {
  telematicsEquipmentId: number;
  oemMake?: string | null;
  oemModel?: string | null;
  oemSerialNumber?: string | null;
  lastSeenApiName?: string | null;
  countryName?: string | null;
  lastLocationDateTime?: string | null;
}

type TabKey = 'mapped' | 'awaiting' | 'rejected' | 'no-match';

const TABS: { value: TabKey; label: string }[] = [
  { value: 'mapped', label: 'Mapped' },
  { value: 'awaiting', label: 'Awaiting Review' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'no-match', label: 'No Match' },
];

function isTabKey(value: string | null): value is TabKey {
  return value === 'mapped' || value === 'awaiting' || value === 'rejected' || value === 'no-match';
}

/** Parse a search param into a positive int id, or undefined when absent/invalid. */
function paramId(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key);
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** Build SelectInput options with a leading "All" entry. */
function withAll(label: string, options: ReadonlyArray<{ id: number; name: string }>): SelectOption[] {
  return [{ value: '', label }, ...options.map((o) => ({ value: o.id, label: o.name }))];
}

export function AssetMappingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: TabKey = isTabKey(rawTab) ? rawTab : 'mapped';

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: TabKey) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <main className="max-w-[1440px] mx-auto px-6 py-8">
      <PageHeader title="Asset Mapping" />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        data-testid="asset-mapping-tabs"
      >
        {TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {activeTab === 'mapped' && (
        <MappingGrid document={SEARCH_MAPPED_QUERY} dataKey="searchMappedAssets" />
      )}
      {activeTab === 'rejected' && (
        <MappingGrid document={SEARCH_REJECTED_QUERY} dataKey="searchRejectedAssets" />
      )}
      {activeTab === 'awaiting' && (
        <MappingGrid document={SEARCH_AWAITING_REVIEW_QUERY} dataKey="searchAwaitingReviewAssets" reviewable />
      )}
      {activeTab === 'no-match' && <NoMatchGrid />}
    </main>
  );
}

interface MappingGridProps {
  document: TypedDocumentNode<Record<string, MappingRow[]>, SearchMappedAssetsQueryVariables>;
  dataKey: 'searchMappedAssets' | 'searchRejectedAssets' | 'searchAwaitingReviewAssets';
  reviewable?: boolean;
}

/** Shared filter bar + results grid for the Mapped and Rejected tabs. */
function MappingGrid({ document, dataKey, reviewable = false }: MappingGridProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const areaId = paramId(searchParams, 'area');
  const countryId = paramId(searchParams, 'country');
  const businessLineId = paramId(searchParams, 'bl');
  const regionId = paramId(searchParams, 'region');
  const brandId = paramId(searchParams, 'brand');
  const modelId = paramId(searchParams, 'model');
  const term = searchParams.get('q') ?? '';

  const [termInput, setTermInput] = useState(term);

  const { data: optionsData, loading: optionsLoading } = useQuery(FILTER_OPTIONS_QUERY, {
    variables: {
      areaId: areaId ?? null,
      countryId: countryId ?? null,
      businessLineId: businessLineId ?? null,
      brandId: brandId ?? null,
    },
  });
  const options = optionsData?.assetSearchFilterOptions;

  const filter = useMemo(
    () => ({
      areaId: areaId ?? null,
      countryId: countryId ?? null,
      businessLineId: businessLineId ?? null,
      regionId: regionId ?? null,
      brandId: brandId ?? null,
      modelId: modelId ?? null,
      searchTerm: term.trim() || null,
    }),
    [areaId, countryId, businessLineId, regionId, brandId, modelId, term],
  );

  const { data, loading, error, refetch } = useQuery(document, { variables: { filter } });
  const rows = (data?.[dataKey] ?? []) as MappingRow[];
  const [reviewedRow, setReviewedRow] = useState<MappingRow | null>(null);

  const updateFilter = useCallback(
    (key: string, value: string, clearChildren: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        clearChildren.forEach((child) => next.delete(child));
        return next;
      });
    },
    [setSearchParams],
  );

  const applyTerm = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const trimmed = termInput.trim();
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
      return next;
    });
  }, [setSearchParams, termInput]);

  const handleClear = useCallback(() => {
    setTermInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      const tab = prev.get('tab');
      if (tab) next.set('tab', tab);
      return next;
    });
  }, [setSearchParams]);

  const columnDefs = useMemo<ColDef<MappingRow>[]>(
    () => [
      { field: 'regionName', headerName: 'Region', flex: 1 },
      {
        field: 'plantName',
        headerName: 'Plant',
        flex: 1,
        cellRenderer: (cell: ICellRendererParams<MappingRow>) => (
          <Link to={`/plants/${cell.data?.plantId}`}>{cell.value}</Link>
        ),
      },
      { field: 'plantCode', headerName: 'Plant Code', flex: 1 },
      {
        field: 'assetCode',
        headerName: 'Asset Code',
        flex: 1,
        cellRenderer: (cell: ICellRendererParams<MappingRow>) => (
          <Link to={`/assets/${cell.data?.assetId}`}>{cell.value}</Link>
        ),
      },
      { field: 'brandName', headerName: 'Brand', flex: 1 },
      { field: 'modelName', headerName: 'Model', flex: 1 },
      { field: 'serialNumber', headerName: 'Serial No.', flex: 1 },
      { field: 'oemMake', headerName: 'OEM Make', flex: 1 },
      { field: 'oemModel', headerName: 'OEM Model', flex: 1 },
      { field: 'oemSerialNumber', headerName: 'OEM Serial No.', flex: 1 },
      { field: 'statusLocalizationKey', headerName: 'Status', flex: 1 },
      ...(reviewable ? [{
        colId: 'review',
        headerName: 'Actions',
        width: 76,
        sortable: false,
        filter: false,
        cellRenderer: (cell: ICellRendererParams<MappingRow>) => (
          <IconButton
            aria-label={`Review mapping for ${cell.data?.assetCode}`}
            size="small"
            onClick={() => cell.data && setReviewedRow(cell.data)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      }] : []),
    ],
    [reviewable],
  );

  return (
    <>
      <LoadingOverlay visible={loading || optionsLoading} />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <TextInput
          search
          value={termInput}
          onChange={(e) => setTermInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyTerm();
          }}
          onBlur={applyTerm}
          placeholder="Search by asset code, serial or OEM serial..."
        />
        <SelectInput
          label="Area"
          fullWidth={false}
          sx={{ minWidth: 160 }}
          value={areaId ? String(areaId) : ''}
          onChange={(e) => updateFilter('area', String(e.target.value), ['country', 'bl', 'region'])}
          options={withAll('All Areas', options?.areas ?? [])}
        />
        <SelectInput
          label="Country"
          fullWidth={false}
          sx={{ minWidth: 160 }}
          value={countryId ? String(countryId) : ''}
          onChange={(e) => updateFilter('country', String(e.target.value), ['bl', 'region'])}
          options={withAll('All Countries', options?.countries ?? [])}
        />
        <SelectInput
          label="Business Line"
          fullWidth={false}
          sx={{ minWidth: 160 }}
          value={businessLineId ? String(businessLineId) : ''}
          onChange={(e) => updateFilter('bl', String(e.target.value), ['region'])}
          options={withAll('All Business Lines', options?.businessLines ?? [])}
        />
        <SelectInput
          label="Region"
          fullWidth={false}
          sx={{ minWidth: 160 }}
          value={regionId ? String(regionId) : ''}
          onChange={(e) => updateFilter('region', String(e.target.value), [])}
          options={withAll('All Regions', options?.regions ?? [])}
        />
        <SelectInput
          label="Brand"
          fullWidth={false}
          sx={{ minWidth: 160 }}
          value={brandId ? String(brandId) : ''}
          onChange={(e) => updateFilter('brand', String(e.target.value), ['model'])}
          options={withAll('All Brands', options?.brands ?? [])}
        />
        <SelectInput
          label="Model"
          fullWidth={false}
          sx={{ minWidth: 160 }}
          value={modelId ? String(modelId) : ''}
          onChange={(e) => updateFilter('model', String(e.target.value), [])}
          options={withAll('All Models', options?.models ?? [])}
        />
        <Button variant="outlined" onClick={handleClear}>
          Clear
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-negative-faded-20 border border-negative rounded text-sm text-negative">
          {error.message}
        </div>
      )}

      {/* Results grid */}
      {rows.length > 0 && (
        <DataGrid<MappingRow> rowData={rows} columnDefs={columnDefs} height="70vh" />
      )}

      {/* Empty state */}
      {data && rows.length === 0 && (
        <div className="text-center py-12 text-foreground-faded">
          No mappings match the current filters.
        </div>
      )}

      <AwaitingReviewDialog
        row={reviewedRow}
        onClose={() => setReviewedRow(null)}
        onSaved={async () => {
          setReviewedRow(null);
          await refetch();
        }}
      />
    </>
  );
}

type MappingCandidate = {
  assetId: number;
  assetCode: string;
  serialNumber?: string | null;
  brandName: string;
  modelName: string;
  plantName: string;
  isEditable: boolean;
};

function AwaitingReviewDialog({
  row,
  onClose,
  onSaved,
}: {
  row: MappingRow | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const { data, loading, error } = useQuery(GET_AWAITING_REVIEW_EQUIPMENT_MATCHES_QUERY, {
    variables: { telematicsEquipmentId: row?.telematicsEquipmentId ?? 0 },
    skip: !row || !isOtherSelected,
  });
  const [saveMapping, { loading: saving }] = useSafeMutation(SAVE_MANUAL_MAPPING_MUTATION);
  const matches = data?.awaitingReviewEquipmentMatches;
  const probableCandidate = row && {
    assetId: row.assetId,
    assetCode: row.assetCode,
    serialNumber: row.serialNumber,
    brandName: row.brandName,
    modelName: row.modelName,
    plantName: row.plantName,
    isEditable: row.isEditable,
  } satisfies MappingCandidate;
  const otherCandidates = ((matches?.otherBrandMatches ?? []) as MappingCandidate[])
    .filter((candidate) => candidate.assetId !== probableCandidate?.assetId);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedAssetId(row?.assetId ?? null);
    setIsOtherSelected(false);
    setSaveError(null);
  }, [row?.assetId]);

  const selectedCandidate = isOtherSelected
    ? otherCandidates.find((candidate) => candidate.assetId === selectedAssetId)
    : probableCandidate;
  const lastLocationLatitude = asFiniteNumber(matches?.lastLocationLatitude);
  const lastLocationLongitude = asFiniteNumber(matches?.lastLocationLongitude);
  const lastLocationDateTime = typeof matches?.lastLocationDateTime === 'string'
    ? matches.lastLocationDateTime
    : null;

  const save = async () => {
    if (!row || !selectedCandidate) return;

    const result = await saveMapping({
      variables: {
        telematicsEquipmentId: row.telematicsEquipmentId,
        assetId: selectedCandidate.assetId,
      },
    });
    if (result.data?.saveManualMapping.success) {
      await onSaved();
      return;
    }

    setSaveError(result.data?.saveManualMapping.error?.message ?? 'The Asset Mapping could not be saved.');
  };

  return (
    <Dialog open={Boolean(row)} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Confirm Asset Mapping</DialogTitle>
      <DialogContent dividers>
        {loading && <p className="text-sm text-foreground-faded">Loading mapping candidates...</p>}
        {error && <p className="text-sm text-negative">{error.message}</p>}
        {saveError && <p className="text-sm text-negative">{saveError}</p>}
        <div className="space-y-5">
          <section>
            <SectionHeading level="h3" className="block mb-2">OEM Equipment</SectionHeading>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-5">
                <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3 md:grid-cols-1">
                  <div><dt className="text-foreground-faded">Make</dt><dd>{row?.oemMake ?? '-'}</dd></div>
                  <div><dt className="text-foreground-faded">Model</dt><dd>{row?.oemModel ?? '-'}</dd></div>
                  <div><dt className="text-foreground-faded">Serial No.</dt><dd>{row?.oemSerialNumber ?? '-'}</dd></div>
                </dl>
                <div>
                  <SectionHeading level="h4" className="block mb-2">Last Location</SectionHeading>
                  <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3 md:grid-cols-1">
                    <div><dt className="text-foreground-faded">Date / Time UTC</dt><dd>{formatUtcDateTime(lastLocationDateTime)}</dd></div>
                    <div><dt className="text-foreground-faded">Longitude</dt><dd>{lastLocationLongitude ?? '-'}</dd></div>
                    <div><dt className="text-foreground-faded">Latitude</dt><dd>{lastLocationLatitude ?? '-'}</dd></div>
                  </dl>
                </div>
              </div>
              {lastLocationLatitude != null && lastLocationLongitude != null && (
                <LocationMap
                  lat={lastLocationLatitude}
                  lng={lastLocationLongitude}
                  label={row?.oemSerialNumber ?? undefined}
                  zoom={10}
                  style={{ height: 280 }}
                />
              )}
            </div>
          </section>

          <section>
            <SectionHeading level="h3" className="block mb-2">Probable Match to FAIM Asset</SectionHeading>
            <div className="flex flex-wrap items-center gap-2">
              {probableCandidate && (
                <ToggleButton
                  value={probableCandidate.assetId}
                  selected={!isOtherSelected}
                  onChange={() => {
                    setIsOtherSelected(false);
                    setSelectedAssetId(probableCandidate.assetId);
                  }}
                  size="small"
                >
                  {probableCandidate.assetCode}
                </ToggleButton>
              )}
              <ToggleButton
                value="other"
                selected={isOtherSelected}
                onChange={() => {
                  setIsOtherSelected(true);
                  setSelectedAssetId(null);
                }}
                size="small"
              >
                Other
              </ToggleButton>
              {isOtherSelected && loading && <span className="text-sm text-foreground-faded">Loading Assets...</span>}
            </div>

            {isOtherSelected && !loading && (
              <div className="mt-3 max-w-xl">
                <SelectInput
                  label="Other Asset"
                  value={selectedAssetId ?? ''}
                  onChange={(event) => setSelectedAssetId(Number(event.target.value))}
                  options={otherCandidates.map((candidate) => ({
                    value: candidate.assetId,
                    label: `${candidate.assetCode} | ${candidate.brandName} ${candidate.modelName} | ${candidate.plantName}`,
                  }))}
                />
              </div>
            )}
          </section>

          {selectedCandidate && (
            <section className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-foreground-faded">
                  <tr><th className="py-2"> </th><th className="py-2">FAIM</th><th className="py-2">OEM</th></tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border"><th className="py-2 text-left">Brand</th><td>{selectedCandidate.brandName}</td><td>{row?.oemMake ?? '-'}</td></tr>
                  <tr className="border-b border-border"><th className="py-2 text-left">Model</th><td>{selectedCandidate.modelName}</td><td>{row?.oemModel ?? '-'}</td></tr>
                  <tr><th className="py-2 text-left">Serial No.</th><td>{selectedCandidate.serialNumber ?? '-'}</td><td>{row?.oemSerialNumber ?? '-'}</td></tr>
                </tbody>
              </table>
              {!selectedCandidate.isEditable && (
                <p className="mt-3 text-sm text-negative">You have read-only access to this Asset's Plant.</p>
              )}
            </section>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={!selectedCandidate?.isEditable || loading || saving}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatUtcDateTime(value?: string | null) {
  if (!value) return '-';
  return `${value.replace('T', ' ').slice(0, 16)} UTC`;
}

function asFiniteNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function NoMatchGrid() {
  const { data, loading, error } = useQuery(SEARCH_NO_MATCH_EQUIPMENT_QUERY);
  const rows = (data?.searchNoMatchEquipment ?? []) as NoMatchRow[];

  const columnDefs = useMemo<ColDef<NoMatchRow>[]>(
    () => [
      { field: 'oemMake', headerName: 'OEM Make', flex: 1 },
      { field: 'oemModel', headerName: 'OEM Model', flex: 1 },
      { field: 'oemSerialNumber', headerName: 'OEM Serial No.', flex: 1 },
      { field: 'lastSeenApiName', headerName: 'Last Seen API', flex: 1 },
      {
        field: 'countryName',
        headerName: 'Country',
        flex: 1,
        valueFormatter: (cell) => cell.value ?? '-',
      },
      {
        field: 'lastLocationDateTime',
        headerName: 'Last Location',
        flex: 1,
        valueFormatter: (cell) => cell.value ?? '-',
      },
    ],
    [],
  );

  return (
    <>
      <LoadingOverlay visible={loading} />

      {error && (
        <div className="mb-4 p-3 bg-negative-faded-20 border border-negative rounded text-sm text-negative">
          {error.message}
        </div>
      )}

      {rows.length > 0 && (
        <DataGrid<NoMatchRow> rowData={rows} columnDefs={columnDefs} height="70vh" />
      )}

      {data && rows.length === 0 && (
        <div className="text-center py-12 text-foreground-faded">
          No unmatched telematics equipment was found.
        </div>
      )}
    </>
  );
}
